import React, { useState, useEffect } from 'react';
import {
    Users,
    UserPlus,
    Search,
    Filter,
    Edit2,
    Trash2,
    Eye,
    EyeOff,
    Calendar,
    Building,
    Phone,
    Mail,
    User,
    UserCheck,
    Shield,
    CheckCircle,
    XCircle,
    AlertTriangle,
    RefreshCw,
    Download,
    Plus,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';

// Country codes data with validation rules
const countryCodes = [
    { code: '91', country: 'India', flag: '🇮🇳', phoneLength: 10, format: 'XXXXXXXXXX' },
    { code: '1', country: 'USA', flag: '🇺🇸', phoneLength: 10, format: 'XXXXXXXXXX' },
    { code: '44', country: 'UK', flag: '🇬🇧', phoneLength: 10, format: 'XXXXXXXXXX' },
    { code: '61', country: 'Australia', flag: '🇦🇺', phoneLength: 9, format: 'XXXXXXXXX' },
    { code: '971', country: 'UAE', flag: '🇦🇪', phoneLength: 9, format: 'XXXXXXXXX' },
    { code: '65', country: 'Singapore', flag: '🇸🇬', phoneLength: 8, format: 'XXXXXXXX' },
    { code: '60', country: 'Malaysia', flag: '🇲🇾', phoneLength: [9, 10], format: 'XXXXXXXXX' },
    { code: '66', country: 'Thailand', flag: '🇹🇭', phoneLength: 9, format: 'XXXXXXXXX' },
    { code: '81', country: 'Japan', flag: '🇯🇵', phoneLength: 10, format: 'XXXXXXXXXX' },
    { code: '82', country: 'South Korea', flag: '🇰🇷', phoneLength: [10, 11], format: 'XXXXXXXXXX' }
];

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [stats, setStats] = useState(null);

    // Filters and pagination
    const [filters, setFilters] = useState({
        search: '',
        userType: '',
        status: '',
        companyName: '',
        page: 1,
        limit: 10
    });

    const [pagination, setPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_records: 0,
        limit: 10
    });

    // Form data
    const [formData, setFormData] = useState({
        whatsappNo: '',
        countryCode: '91',
        email: '',
        name: '',
        companyId: '',
        companyName: '',
        userType: 'employee',
        startDate: '',
        endDate: '',
        status: 'active'
    });

    const [formErrors, setFormErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState('');

    useEffect(() => {
        fetchUsers();
        fetchStats();
        fetchCompanies();
    }, [filters]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams(filters);
            const response = await fetch(`https://api.zapbooksai.subraps.com/api/auth/user-access?${params}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            const data = await response.json();
            if (data.success) {
                setUsers(data.data.users || []);
                setPagination(data.data.pagination || {
                    current_page: 1,
                    total_pages: 1,
                    total_records: 0,
                    limit: 10
                });
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await fetch('https://api.zapbooksai.subraps.com/api/auth/super-admin-stats', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            const data = await response.json();
            if (data.success) {
                setStats(data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchCompanies = async () => {
        try {
            // Get unique companies from user_access
            const response = await fetch('https://api.zapbooksai.subraps.com/api/auth/companies-list', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            const data = await response.json();
            if (data.success) {
                setCompanies(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            // Fallback: extract companies from users if API not available
            const uniqueCompanies = [...new Set(users
                .filter(user => user.company_name)
                .map(user => user.company_name))]
                .map(name => ({ company_name: name }));
            setCompanies(uniqueCompanies);
        }
    };

    const validatePhoneNumber = (phoneNumber, countryCode) => {
        const country = countryCodes.find(c => c.code === countryCode);
        if (!country) return { isValid: false, message: 'Invalid country code' };

        const cleanPhone = phoneNumber.replace(/\D/g, ''); // Remove all non-digits

        if (Array.isArray(country.phoneLength)) {
            // Multiple valid lengths (e.g., Malaysia: 9-10 digits)
            const isValidLength = country.phoneLength.includes(cleanPhone.length);
            if (!isValidLength) {
                return {
                    isValid: false,
                    message: `Phone number for ${country.country} must be ${country.phoneLength.join(' or ')} digits`
                };
            }
        } else {
            // Single valid length
            if (cleanPhone.length !== country.phoneLength) {
                return {
                    isValid: false,
                    message: `Phone number for ${country.country} must be exactly ${country.phoneLength} digits`
                };
            }
        }

        // Check if it's all digits
        if (!/^\d+$/.test(cleanPhone)) {
            return {
                isValid: false,
                message: 'Phone number must contain only digits'
            };
        }

        return { isValid: true, message: '' };
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.whatsappNo) {
            errors.whatsappNo = 'WhatsApp number is required';
        } else {
            const validation = validatePhoneNumber(formData.whatsappNo, formData.countryCode);
            if (!validation.isValid) {
                errors.whatsappNo = validation.message;
            }
        }

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!formData.userType) {
            errors.userType = 'User type is required';
        }

        if (!formData.startDate) {
            errors.startDate = 'Start date is required';
        }

        if (!formData.endDate) {
            errors.endDate = 'End date is required';
        }

        if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
            errors.endDate = 'End date must be after start date';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    useEffect(() => {
        if (editingUser) {
            // Parse the WhatsApp number to extract country code and number
            const whatsappNo = editingUser.whatsapp_no;
            let countryCode = '91'; // default
            let phoneNumber = whatsappNo;

            // Try to extract country code from the stored number
            const countryCodeObj = countryCodes.find(cc => whatsappNo.startsWith(cc.code));
            if (countryCodeObj) {
                countryCode = countryCodeObj.code;
                phoneNumber = whatsappNo.substring(countryCode.length);
            }

            // Format dates for datetime-local input
            const formatDateForInput = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                // Get timezone offset and adjust
                const offset = date.getTimezoneOffset();
                const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
                return adjustedDate.toISOString().slice(0, 16);
            };

            setFormData({
                whatsappNo: phoneNumber,
                countryCode: countryCode,
                email: editingUser.email || '',
                name: editingUser.name || '',
                companyId: editingUser.company_id || '',
                companyName: editingUser.company_name || '',
                userType: editingUser.user_type || 'employee',
                startDate: formatDateForInput(editingUser.start_date),
                endDate: formatDateForInput(editingUser.end_date),
                status: editingUser.status || 'active'
            });

            // Clear any existing errors
            setFormErrors({});
        }
    }, [editingUser]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const url = editingUser
                ? `https://api.zapbooksai.subraps.com/api/auth/user-access/${editingUser.id}`
                : 'https://api.zapbooksai.subraps.com/api/auth/create-user-access-enhanced';

            const method = editingUser ? 'PUT' : 'POST';

            // For updates, we don't send whatsappNo and countryCode since they can't be changed
            const requestData = editingUser
                ? {
                    email: formData.email,
                    name: formData.name,
                    companyId: formData.companyId,
                    companyName: formData.companyName,
                    userType: formData.userType,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    status: formData.status
                }
                : formData;

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.success) {
                if (!editingUser && data.data?.defaultPassword) {
                    setGeneratedPassword(data.data.defaultPassword);
                    setShowPassword(true);
                }
                resetForm();
                fetchUsers();
                fetchStats();
                fetchCompanies();
                alert(editingUser ? 'User updated successfully!' : 'User created successfully!');
            } else {
                alert(data.message || `Error ${editingUser ? 'updating' : 'creating'} user`);
            }
        } catch (error) {
            console.error(`Error ${editingUser ? 'updating' : 'creating'} user:`, error);
            alert(`Error ${editingUser ? 'updating' : 'creating'} user. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) {
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`https://api.zapbooksai.subraps.com/api/auth/user-access/${userId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            const data = await response.json();

            if (data.success) {
                fetchUsers();
                fetchStats();
                fetchCompanies();
                alert('User deleted successfully!');
            } else {
                alert(data.message || 'Error deleting user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Error deleting user. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            whatsappNo: '',
            countryCode: '91',
            email: '',
            name: '',
            companyId: '',
            companyName: '',
            userType: 'employee',
            startDate: '',
            endDate: '',
            status: 'active'
        });
        setFormErrors({});
        setEditingUser(null);
        setShowCreateForm(false);
        setShowPassword(false);
        setGeneratedPassword('');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Generate suggested password when name changes
        if (name === 'name' && value) {
            const suggestedPassword = `${value.toLowerCase().replace(/\s+/g, '')}@123`;
            setGeneratedPassword(suggestedPassword);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
            inactive: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
            suspended: { color: 'bg-red-100 text-red-800', icon: AlertTriangle }
        };

        const config = statusConfig[status] || statusConfig.inactive;
        const StatusIcon = config.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <StatusIcon className="w-3 h-3" />
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const getUserTypeBadge = (userType) => {
        const typeConfig = {
            approver: { color: 'bg-blue-100 text-blue-800', icon: UserCheck },
            employee: { color: 'bg-green-100 text-green-800', icon: User }
        };

        const config = typeConfig[userType] || typeConfig.employee;
        const TypeIcon = config.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <TypeIcon className="w-3 h-3" />
                {userType.charAt(0).toUpperCase() + userType.slice(1)}
            </span>
        );
    };

    // Calculate stats based on whether filters are applied
    const hasActiveFilters = filters.search || filters.userType || filters.status || filters.companyName;

    const calculatedStats = hasActiveFilters ? {
        // When filters are active, show filtered totals
        totalUsers: pagination.total_records || 0,
        activeUsers: users.filter(user => user.status === 'active').length,
        approvers: users.filter(user => user.user_type === 'approver').length,
        totalCompanies: filters.companyName ? 1 : companies.length || 0
    } : (stats ? {
        // When no filters, show global stats from API
        totalUsers: stats.userAccessStats ?
            stats.userAccessStats.reduce((total, stat) => total + stat.count, 0) : 0,
        activeUsers: stats.userAccessStats ?
            stats.userAccessStats
                .filter(stat => stat.status === 'active')
                .reduce((total, stat) => total + stat.count, 0) : 0,
        approvers: stats.userAccessStats ?
            stats.userAccessStats
                .filter(stat => stat.user_type === 'approver')
                .reduce((total, stat) => total + stat.count, 0) : 0,
        totalCompanies: stats.totalCompanies || 0
    } : {
        // Fallback when no stats available
        totalUsers: pagination.total_records || 0,
        activeUsers: 0,
        approvers: 0,
        totalCompanies: companies.length || 0
    });

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Shield className="w-6 h-6 text-purple-600" />
                        User Management
                    </h1>
                    <p className="text-gray-600 mt-1">Manage user access and permissions</p>
                </div>
                <button
                    onClick={() => setShowCreateForm(true)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                    <UserPlus className="w-4 h-4" />
                    Create User
                </button>
            </div>

            {/* Stats Cards */}
            {calculatedStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Total Users</p>
                                <p className="text-2xl font-bold text-gray-900">{calculatedStats.totalUsers}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Active Users</p>
                                <p className="text-2xl font-bold text-gray-900">{calculatedStats.activeUsers}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <UserCheck className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Approvers</p>
                                <p className="text-2xl font-bold text-gray-900">{calculatedStats.approvers}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                                <Building className="w-5 h-5 text-teal-600" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Companies</p>
                                <p className="text-2xl font-bold text-gray-900">{calculatedStats.totalCompanies}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={filters.search}
                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
                        <select
                            value={filters.userType}
                            onChange={(e) => setFilters(prev => ({ ...prev, userType: e.target.value, page: 1 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value="">All Types</option>
                            <option value="approver">Approver</option>
                            <option value="employee">Employee</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="suspended">Suspended</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                        <select
                            value={filters.companyName}
                            onChange={(e) => setFilters(prev => ({ ...prev, companyName: e.target.value, page: 1 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value="">All Companies</option>
                            {companies.map((company, index) => (
                                <option key={index} value={company.company_name}>
                                    {company.company_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-end space-x-2">
                        <button
                            onClick={() => {
                                setFilters({
                                    search: '',
                                    userType: '',
                                    status: '',
                                    companyName: '',
                                    page: 1,
                                    limit: 10
                                });
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Reset
                        </button>
                        <select
                            value={filters.limit}
                            onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                            <option value={10}>10 per page</option>
                            <option value={25}>25 per page</option>
                            <option value={50}>50 per page</option>
                            <option value={100}>100 per page</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Period</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <div className="flex items-center justify-center">
                                            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mr-2" />
                                            Loading users...
                                        </div>
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                                    <User className="w-5 h-5 text-gray-600" />
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                                    <p className="text-sm text-gray-500">ID: {user.id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1">
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                                                    +{user.whatsapp_no}
                                                </div>
                                                {user.email && (
                                                    <div className="flex items-center text-sm text-gray-600">
                                                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                                                        {user.email}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                {user.company_name ? (
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{user.company_name}</p>
                                                        {user.company_id && (
                                                            <p className="text-sm text-gray-500">ID: {user.company_id}</p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-gray-400">No company</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {getUserTypeBadge(user.user_type)}
                                        </td>
                                        <td className="px-6 py-4">
                                            {getStatusBadge(user.status)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1">
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                                    {formatDateTime(user.start_date)}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    to {formatDateTime(user.end_date)}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => setEditingUser(user)}
                                                    className="text-gray-600 hover:text-gray-900 p-1"
                                                    title="Edit user"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user.id)}
                                                    className="text-red-600 hover:text-red-900 p-1"
                                                    title="Delete user"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {pagination.total_pages > 1 && (
                    <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-700">
                            <span>
                                Showing {((pagination.current_page - 1) * pagination.limit) + 1} to{' '}
                                {Math.min(pagination.current_page * pagination.limit, pagination.total_records)} of{' '}
                                {pagination.total_records} results
                            </span>
                        </div>

                        <div className="flex items-center space-x-2">
                            {/* Previous Button */}
                            <button
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={pagination.current_page <= 1}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </button>

                            {/* Page Numbers */}
                            <div className="flex items-center space-x-1">
                                {/* First page */}
                                {pagination.current_page > 3 && (
                                    <>
                                        <button
                                            onClick={() => setFilters(prev => ({ ...prev, page: 1 }))}
                                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                        >
                                            1
                                        </button>
                                        {pagination.current_page > 4 && (
                                            <span className="px-2 py-1 text-sm text-gray-500">...</span>
                                        )}
                                    </>
                                )}

                                {/* Current page and neighbors */}
                                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                                    let pageNum;
                                    if (pagination.total_pages <= 5) {
                                        pageNum = i + 1;
                                    } else if (pagination.current_page <= 3) {
                                        pageNum = i + 1;
                                    } else if (pagination.current_page >= pagination.total_pages - 2) {
                                        pageNum = pagination.total_pages - 4 + i;
                                    } else {
                                        pageNum = pagination.current_page - 2 + i;
                                    }

                                    if (pageNum < 1 || pageNum > pagination.total_pages) return null;

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setFilters(prev => ({ ...prev, page: pageNum }))}
                                            className={`px-3 py-1 text-sm border rounded-md transition-colors ${pageNum === pagination.current_page
                                                    ? 'bg-purple-600 text-white border-purple-600'
                                                    : 'border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}

                                {/* Last page */}
                                {pagination.current_page < pagination.total_pages - 2 && (
                                    <>
                                        {pagination.current_page < pagination.total_pages - 3 && (
                                            <span className="px-2 py-1 text-sm text-gray-500">...</span>
                                        )}
                                        <button
                                            onClick={() => setFilters(prev => ({ ...prev, page: pagination.total_pages }))}
                                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                        >
                                            {pagination.total_pages}
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Next Button */}
                            <button
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={pagination.current_page >= pagination.total_pages}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Create/Edit User Modal */}
            {(showCreateForm || editingUser) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <UserPlus className="w-5 h-5 text-purple-600" />
                                {editingUser ? 'Edit User' : 'Create New User'}
                            </h2>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* WhatsApp Number with Country Code */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Country Code *
                                    </label>
                                    <select
                                        name="countryCode"
                                        value={formData.countryCode}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${editingUser ? 'bg-gray-100 cursor-not-allowed' : ''
                                            }`}
                                        required
                                        disabled={editingUser || loading}
                                    >
                                        {countryCodes.map((country) => (
                                            <option key={country.code} value={country.code}>
                                                {country.flag} +{country.code} {country.country}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        WhatsApp Number *
                                    </label>
                                    <input
                                        type="text"
                                        name="whatsappNo"
                                        value={formData.whatsappNo}
                                        onChange={handleInputChange}
                                        placeholder={(() => {
                                            const country = countryCodes.find(c => c.code === formData.countryCode);
                                            return country ? `Enter ${country.format.replace(/X/g, '0')}` : 'Enter phone number';
                                        })()}
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${formErrors.whatsappNo ? 'border-red-300' : 'border-gray-300'
                                            } ${editingUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        required
                                        disabled={editingUser || loading}
                                    />
                                    {formErrors.whatsappNo && (
                                        <p className="text-red-600 text-sm mt-1">{formErrors.whatsappNo}</p>
                                    )}
                                    <p className="text-gray-500 text-xs mt-1">
                                        {editingUser
                                            ? `Current number: +${formData.countryCode}${formData.whatsappNo} (cannot be changed)`
                                            : (() => {
                                                const country = countryCodes.find(c => c.code === formData.countryCode);
                                                const lengthText = Array.isArray(country?.phoneLength)
                                                    ? `${country.phoneLength.join(' or ')} digits`
                                                    : `${country?.phoneLength || 10} digits`;
                                                return `Full number will be: +${formData.countryCode}${formData.whatsappNo} (${lengthText} required)`;
                                            })()
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Name and Email */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter full name"
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${formErrors.name ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                        required
                                    />
                                    {formErrors.name && (
                                        <p className="text-red-600 text-sm mt-1">{formErrors.name}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${formErrors.email ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                    />
                                    {formErrors.email && (
                                        <p className="text-red-600 text-sm mt-1">{formErrors.email}</p>
                                    )}
                                </div>
                            </div>

                            {/* Company Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Company ID
                                    </label>
                                    <input
                                        type="text"
                                        name="companyId"
                                        value={formData.companyId}
                                        onChange={handleInputChange}
                                        placeholder="Enter company ID (optional)"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleInputChange}
                                        placeholder="Enter company name (optional)"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                </div>
                            </div>

                            {/* User Type and Status */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        User Type *
                                    </label>
                                    <select
                                        name="userType"
                                        value={formData.userType}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        required
                                    >
                                        <option value="employee">Employee</option>
                                        <option value="approver">Approver</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status *
                                    </label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        required
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="suspended">Suspended</option>
                                    </select>
                                </div>
                            </div>

                            {/* Access Period */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Start Date *
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${formErrors.startDate ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                        required
                                    />
                                    {formErrors.startDate && (
                                        <p className="text-red-600 text-sm mt-1">{formErrors.startDate}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        End Date *
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${formErrors.endDate ? 'border-red-300' : 'border-gray-300'
                                            }`}
                                        required
                                    />
                                    {formErrors.endDate && (
                                        <p className="text-red-600 text-sm mt-1">{formErrors.endDate}</p>
                                    )}
                                </div>
                            </div>

                            {/* Generated Password Display */}
                            {generatedPassword && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-sm font-medium text-blue-900">Generated Password</h4>
                                            <p className="text-sm text-blue-700 mt-1">
                                                Password for {formData.name}:
                                                <span className="font-mono ml-2">
                                                    {showPassword ? generatedPassword : '••••••••'}
                                                </span>
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="text-blue-600 hover:text-blue-800"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Form Actions */}
                            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                    {loading ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Plus className="w-4 h-4" />
                                    )}
                                    {editingUser ? 'Update User' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;